import React, { useState, useEffect } from 'react';
import { useFirestore } from '../../hooks/useFirestore';
import { Pizza } from '../../types';
import Map from '../../components/Map';
import { geocodeAddress } from '../../utils/geocoding';
import { useAuth } from '../../hooks/useAuth';
import { db, storage } from '../../lib/firebase';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  serverTimestamp, 
  runTransaction, 
  getDoc, 
  getDocs, 
  query, 
  where 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage';
import { urls } from '../../config/urls';

interface PizzeriaWizardProps {
  isVisible: boolean;
  onClose: () => void;
}

interface PlaceType {
  address: string;
  lat: number;
  lng: number;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

interface SaveDataResult {
  success: boolean;
  error?: string;
  pizzeriaId?: string;
}

async function uploadToFirebaseStorage(file: File): Promise<string> {
  try {
    if (file.size > MAX_FILE_SIZE) {
      throw new Error('Il file è troppo grande. Dimensione massima: 10MB');
    }

    const timestamp = Date.now();
    const safeFileName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
    const finalFileName = `pizzerie/${timestamp}_${safeFileName}`;
    
    // Crea il riferimento allo storage
    const storageRef = ref(storage, finalFileName);

    // Upload del file
    console.log('Inizio upload...');
    const snapshot = await uploadBytes(storageRef, file);
    console.log('Upload completato:', snapshot.metadata.fullPath);

    // Ottieni l'URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log('URL ottenuto:', downloadURL);

    return downloadURL;

  } catch (error) {
    console.error('Errore durante l\'upload:', error);
    throw error instanceof Error ? error : new Error('Errore sconosciuto durante l\'upload');
  }
}

export function PizzeriaWizard({ isVisible, onClose }: PizzeriaWizardProps) {
  const { user } = useAuth();
  const { searchPizzas } = useFirestore();
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 6;
  
  // Form states
  const [pizzeriaName, setPizzeriaName] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [location, setLocation] = useState({ lat: 41.9028, lng: 12.4964 });
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const [availablePizzas, setAvailablePizzas] = useState<Pizza[]>([]);
  const [selectedPizza, setSelectedPizza] = useState('');
  const [addressInput, setAddressInput] = useState('');
  const [city, setCity] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState<PlaceType[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Carica le pizze disponibili all'avvio
  useEffect(() => {
    const loadPizzas = async () => {
      try {
        const pizzas = await searchPizzas({ type: 'pizza', query: '' });
        setAvailablePizzas(pizzas);
      } catch (error) {
        console.error('Errore nel caricamento delle pizze:', error);
      }
    };
    loadPizzas();
  }, []);

  // Validazione per ogni step
  const validateStep = (stepNumber: number): boolean => {
    switch (stepNumber) {
      case 1:
        return true; // Primo step è solo informativo
      case 2:
        return pizzeriaName.trim() !== '' && description.trim() !== '';
      case 3:
        return address.trim() !== '' && city.trim() !== '';
      case 4:
        return !!photo;
      case 5:
        return selectedPizza !== '';
      default:
        return true;
    }
  };

  // Gestione cambio step
  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      setSaveError(null); // Reset errore quando si avanza
    } else {
      setSaveError('Completa tutti i campi prima di procedere');
    }
  };

  // Definizione delle funzioni prima degli step
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (validateFile(file)) {
        setPhoto(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setPhotoPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const validateFile = (file: File): boolean => {
    setFileError(null);

    if (!file.type.startsWith('image/')) {
      setFileError('Il file deve essere un\'immagine (PNG, JPG o GIF)');
      return false;
    }

    return true;
  };

  // Ora definiamo gli steps
  const steps = [
    {
      title: "Nuova Pizzeria",
      content: (
        <>
          <p className="text-gray-600 text-center mb-4">
            Benvenuto nella procedura guidata per aggiungere una nuova pizzeria.
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-600 mb-4">
            <li>Inserisci le informazioni del tuo locale</li>
            <li>Carica una foto rappresentativa</li>
            <li>Scegli le pizze da pubblicare</li>
            <li>Verifica e conferma i dati</li>
          </ul>
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 mb-6">
            <p className="text-yellow-700 text-center font-medium">
              ℹ️ Ricordati che per pubblicare una pizza nel menu è necessario uno spicchio.
              Controlla il tuo credito residuo prima di procedere.
            </p>
          </div>
        </>
      )
    },
    {
      title: "Informazioni Base",
      content: (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome della Pizzeria
            </label>
            <input
              type="text"
              value={pizzeriaName}
              onChange={(e) => setPizzeriaName(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Es: La Vegana Felice"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrizione
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              rows={4}
              placeholder="Descrivi la tua pizzeria e cosa la rende speciale..."
            />
          </div>
        </div>
      )
    },
    {
      title: "Localizzazione",
      content: (
        <div className="space-y-4">
          <div className="relative z-20">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Indirizzo
            </label>
            <input
              type="text"
              value={addressInput}
              onChange={(e) => handleAddressInput(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Inizia a scrivere l'indirizzo..."
            />
            
            {/* Suggerimenti indirizzi */}
            {addressSuggestions.length > 0 && (
              <div className="absolute w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-auto">
                {addressSuggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSelectAddress(suggestion)}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                  >
                    {suggestion.address}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative z-10 h-[300px] rounded-lg overflow-hidden border">
            <Map
              locations={[{ id: '1', ...location, name: pizzeriaName || 'La tua pizzeria' }]}
              center={{ ...location, zoom: 15 }}
              onMarkerClick={() => {}}
              onBoundsChange={() => {}}
            />
          </div>
        </div>
      )
    },
    {
      title: "Foto della Pizzeria",
      content: (
        <div className="space-y-4">
          <div 
            className="flex justify-center items-center w-full"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <label 
              className={`w-full flex flex-col items-center px-4 py-6 bg-white rounded-lg border-2 
                ${photoPreview ? 'border-green-200' : fileError ? 'border-red-200' : 'border-dashed border-gray-300'} 
                cursor-pointer hover:bg-gray-50 transition-colors duration-200 ease-in-out
                relative group`}
            >
              <div className="flex flex-col items-center justify-center">
                {photoPreview ? (
                  <div className="relative">
                    <img 
                      src={photoPreview} 
                      alt="Preview" 
                      className="w-64 h-64 object-cover rounded-lg mb-4"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 rounded-lg transition-all duration-200 flex items-center justify-center">
                      <p className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        Clicca o trascina per cambiare
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Clicca per caricare</span> o trascina un'immagine
                    </p>
                    <p className="text-xs text-gray-500">PNG, JPG o GIF (max. 10MB)</p>
                  </>
                )}
              </div>
              <input 
                type="file" 
                className="hidden" 
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file && validateFile(file)) {
                    setPhoto(file);
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setPhotoPreview(reader.result as string);
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
            </label>
          </div>

          {fileError && (
            <div className="text-center text-sm text-red-600">
              ⚠️ {fileError}
            </div>
          )}
        </div>
      )
    },
    {
      title: "La tua Pizza",
      content: (
        <div className="space-y-4">
          <p className="text-gray-600 mb-4">
            Seleziona una pizza dal nostro database per aggiungerla al tuo menu:
          </p>
          <select
            value={selectedPizza}
            onChange={(e) => setSelectedPizza(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="">Seleziona una pizza...</option>
            {availablePizzas.map((pizza) => (
              <option key={pizza.id} value={pizza.id}>
                {pizza.name}
              </option>
            ))}
          </select>
          {selectedPizza && (
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-green-700 text-center">
                Ottima scelta! Questa pizza sarà aggiunta al tuo menu.
              </p>
            </div>
          )}
        </div>
      )
    },
    {
      title: "Riepilogo e Conferma",
      content: (
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
          <h3 className="text-lg font-medium text-gray-900 sticky top-0 bg-white py-2 z-10 border-b">
            Conferma i dati inseriti
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Colonna 1: Informazioni Base */}
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <h4 className="font-medium text-gray-700 mb-2 text-sm uppercase tracking-wide">
                  Informazioni Base
                </h4>
                <div className="bg-white p-3 rounded shadow-sm">
                  <p className="text-gray-600 text-sm">
                    <span className="font-medium">Nome:</span><br />
                    {pizzeriaName}
                  </p>
                  <p className="text-gray-600 text-sm mt-2">
                    <span className="font-medium">Descrizione:</span><br />
                    <span className="text-xs leading-relaxed">{description}</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Colonna 2: Localizzazione */}
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <h4 className="font-medium text-gray-700 mb-2 text-sm uppercase tracking-wide">
                  Localizzazione
                </h4>
                <div className="bg-white p-3 rounded shadow-sm">
                  <p className="text-gray-600 text-sm">
                    <span className="font-medium">Indirizzo:</span><br />
                    {address}
                  </p>
                  <p className="text-gray-600 text-sm mt-2">
                    <span className="font-medium">Città:</span><br />
                    {city}
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-3">
                <h4 className="font-medium text-gray-700 mb-2 text-sm uppercase tracking-wide">
                  Foto
                </h4>
                <div className="bg-white p-3 rounded shadow-sm">
                  {photoPreview ? (
                    <div className="flex items-center">
                      <img 
                        src={photoPreview} 
                        alt="Preview" 
                        className="w-16 h-16 object-cover rounded"
                      />
                      <span className="text-green-600 text-sm ml-3 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Caricata
                      </span>
                    </div>
                  ) : (
                    <p className="text-yellow-600 text-sm flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      Nessuna foto
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Colonna 3: Pizza e Note */}
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <h4 className="font-medium text-gray-700 mb-2 text-sm uppercase tracking-wide">
                  Pizza Selezionata
                </h4>
                <div className="bg-white p-3 rounded shadow-sm">
                  <p className="text-gray-600 text-sm">
                    {availablePizzas.find(p => p.id === selectedPizza)?.name || 
                      'Nessuna pizza selezionata'}
                  </p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-yellow-700 text-sm flex items-center">
                  <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Verifica attentamente i dati prima di procedere
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  // Implementazione dei metodi
  const handleAddressInput = async (input: string) => {
    setAddressInput(input);
    
    if (!input || input.length < 3) {
      setAddressSuggestions([]);
      return;
    }

    setIsLoadingSuggestions(true);
    try {
      const query = encodeURIComponent(`${input}, Italy`);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=5`,
        {
          headers: {
            'User-Agent': 'PizzaVeg App (https://pizzaveg.it)',
            'Accept-Language': 'it'
          }
        }
      );

      const data = await response.json();
      const suggestions = data.map((item: any) => ({
        address: item.display_name,
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lon)
      }));

      setAddressSuggestions(suggestions);
    } catch (error) {
      console.error('Errore nel caricamento dei suggerimenti:', error);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const handleSelectAddress = async (place: PlaceType) => {
    setAddressInput(place.address);
    setAddress(place.address);
    setLocation({ lat: place.lat, lng: place.lng });
    setAddressSuggestions([]);

    const addressParts = place.address.split(',');
    if (addressParts.length > 1) {
      setCity(addressParts[addressParts.length - 2].trim());
    }
  };

  const handleSaveData = async (): Promise<SaveDataResult> => {
    if (!user) {
      return { success: false, error: 'Utente non autenticato' };
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      let imageUrl = '';

      if (photo) {
        try {
          imageUrl = await uploadToFirebaseStorage(photo);
          console.log('URL immagine finale:', imageUrl);
        } catch (error) {
          console.error('Errore nel caricamento dell\'immagine:', error);
          setSaveError('Errore nel caricamento dell\'immagine');
          return { success: false, error: 'Errore nel caricamento dell\'immagine' };
        }
      }

      // 1. Crea la pizzeria
      const pizzeriaRef = collection(db, 'pizzerie');
      const pizzeriaData = {
        name: pizzeriaName,
        description,
        address,
        city,
        lat: location.lat,
        lng: location.lng,
        imageUrl,
        email: user.email,
        phone: '',
        createdAt: serverTimestamp(),
        isVerified: true,
        pizzas: [selectedPizza],
        role: 'pizzeria',
        ownerId: user.uid
      };

      const pizzeriaDoc = await addDoc(pizzeriaRef, pizzeriaData);
      console.log('✨ Pizzeria creata:', pizzeriaDoc.id);

      // 2. Aggiungi la pizza al menu
      const pizzaInMenuRef = collection(db, 'pizzeinmenu');
      const now = new Date();
      const expiryDate = new Date(now.setMonth(now.getMonth() + 1));
      
      const pizzaInMenuData = {
        pizzaId: selectedPizza,
        pizzeriaId: pizzeriaDoc.id,
        addedAt: serverTimestamp(),
        expiresAt: expiryDate.toISOString(),
        isHidden: false
      };

      const pizzaInMenuDoc = await addDoc(pizzaInMenuRef, pizzaInMenuData);
      console.log('🍕 Pizza aggiunta al menu:', pizzaInMenuDoc.id);

      // 3. Controlla gli spicchi dell'utente
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data();
      const currentSlices = userData?.slices || 0;

      // Se l'utente ha spicchi disponibili, ne sottrae uno
      if (currentSlices > 0) {
        console.log('🔄 Sottraggo uno spicchio');
        await updateDoc(userRef, {
          slices: currentSlices - 1,
          updatedAt: serverTimestamp()
        });

        // Registra la transazione di consumo spicchio
        const sliceTransactionRef = collection(db, 'sliceTransactions');
        await addDoc(sliceTransactionRef, {
          amount: -1,
          createdAt: serverTimestamp(),
          type: 'consume_slice',
          userId: user.uid,
          note: 'Spicchio consumato per pubblicazione pizza'
        });
      }

      return { success: true, pizzeriaId: pizzeriaDoc.id };

    } catch (error) {
      console.error('Errore nel salvataggio dei dati:', error);
      const errorMessage = error instanceof Error ? error.message : 'Errore durante il salvataggio';
      setSaveError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsSaving(false);
    }
  };

  const validateData = (): { isValid: boolean; error?: string } => {
    if (!pizzeriaName.trim()) {
      return { isValid: false, error: 'Il nome della pizzeria è obbligatorio' };
    }
    if (!description.trim()) {
      return { isValid: false, error: 'La descrizione è obbligatoria' };
    }
    if (!address.trim() || !city.trim()) {
      return { isValid: false, error: 'L\'indirizzo completo è obbligatorio' };
    }
    if (!selectedPizza) {
      return { isValid: false, error: 'Seleziona almeno una pizza' };
    }
    return { isValid: true };
  };

  const handleComplete = async () => {
    const validation = validateData();
    if (!validation.isValid) {
      setSaveError(validation.error || 'Errore di validazione');
      return;
    }

    const result = await handleSaveData();
    if (result.success) {
      onClose();
      window.location.reload();
    } else {
      setSaveError(result.error || 'Errore durante il salvataggio');
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl p-8 max-w-2xl w-11/12 relative shadow-xl">
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 text-2xl"
        >
          &times;
        </button>
        
        {/* Step indicators */}
        <div className="flex justify-center mb-8">
          {Array.from({ length: totalSteps }).map((_, index) => (
            <div
              key={index}
              className={`w-3 h-3 rounded-full mx-1 ${
                index + 1 === currentStep
                  ? 'bg-green-500'
                  : index + 1 < currentStep
                  ? 'bg-green-200'
                  : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        
        {/* Content */}
        <div className="min-h-[300px]">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            {steps[currentStep - 1].title}
          </h2>
          
          {steps[currentStep - 1].content}
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between mt-8">
          <button
            onClick={() => setCurrentStep(prev => Math.max(prev - 1, 1))}
            className={`px-6 py-2 rounded-lg transition-colors ${
              currentStep === 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            disabled={currentStep === 1 || isSaving}
          >
            Precedente
          </button>
          
          <button
            onClick={() => {
              if (currentStep === totalSteps) {
                handleComplete();
              } else {
                handleNextStep();
              }
            }}
            disabled={isSaving}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            {currentStep === totalSteps 
              ? (isSaving ? 'Salvataggio...' : 'Completa') 
              : 'Prossimo'}
          </button>
        </div>

        {saveError && (
          <div className="mt-4 text-center text-sm text-red-600">
            ⚠️ {saveError}
          </div>
        )}
      </div>
    </div>
  );
} 